import {format} from 'date-fns';
import {ru} from 'date-fns/locale/ru';

import {formatNumberThousands} from '@specials/utils';

import {DateUtils} from './DateUtils';
import {DeclineUtils} from './DeclineUtils';
import {NumberUtils} from './NumberUtils';

export enum TimeFormat {
  Days = 'Days',
  Hours = 'Hours',
  Minutes = 'Minutes',
  Seconds = 'Seconds',
}

export class FormatUtils {
  static formatDailyMiniGameTimer(timeInSeconds: number): string {
    const hours = FormatUtils.pad(DateUtils.getHourRemainderBySeconds(timeInSeconds));
    const minutes = FormatUtils.pad(DateUtils.getMinutesRemainderBySeconds(timeInSeconds));

    return `${hours}:${minutes}`;
  }

  static formatLeaderBoardTimer(timeInSeconds: number): string {
    const days = DateUtils.secondsToDays(timeInSeconds);

    if (days > 0) {
      return `${days} ${DeclineUtils.declineDays(days)}`;
    }

    const hours = FormatUtils.pad(DateUtils.getHourRemainderBySeconds(timeInSeconds));
    const minutes = FormatUtils.pad(DateUtils.getMinutesRemainderBySeconds(timeInSeconds));
    const seconds = FormatUtils.pad(DateUtils.getSecondsRemainderBySeconds(timeInSeconds));

    return `${hours}:${minutes}:${seconds}`;
  }

  static formatFrozenImprovementTimer(timeInSeconds: number): string {
    const hours = FormatUtils.pad(DateUtils.getHourRemainderBySeconds(timeInSeconds));
    const minutes = FormatUtils.pad(DateUtils.getMinutesRemainderBySeconds(timeInSeconds));
    const seconds = FormatUtils.pad(DateUtils.getSecondsRemainderBySeconds(timeInSeconds));

    return `${hours}:${minutes}:${seconds}`;
  }

  static formatNumberByDigits(data: string | number, startFormatFromLength = 5): string {
    const normalizedData = `${data}`.replace(/\s/g, '');

    if (normalizedData.length < startFormatFromLength) return normalizedData;

    return formatNumberThousands(Number(normalizedData));
  }

  static pad(n: number): string {
    if (n >= 10) {
      return n.toString();
    }

    if (n < 0) {
      return n.toString();
    }

    return '0' + n;
  }

  static formatDateToText(date: string | Date): string {
    return format(new Date(date), 'd MMMM', {locale: ru});
  }

  static formatPoints(points: number): string {
    if (NumberUtils.inRange(1000, 9999, points)) {
      const str = points.toString();
      return `${str[0]} ${str.slice(1)}`;
    }

    return formatNumberThousands(points);
  }

  static formatNumberAndAddSuffixSI(value: number): string {
    if (value < 1) {
      const zeroRounded = Math.floor(value * 100) / 100;
      return zeroRounded.toString();
    }

    const SI_SYMBOL = ['', 'K', 'M', 'B', 'T', 'P', 'E'];

    const tier = Math.floor(Math.log10(Math.abs(value)) / 3);
    if (tier === 0) {
      const hundredsRounded = Math.floor(value);
      return hundredsRounded.toString();
    }

    const suffix = SI_SYMBOL[tier];

    if (!suffix) {
      return '999E+';
    }

    const scale = Math.pow(10, tier * 3);
    const scaled = value / scale;
    const scaledRounded = Math.floor(scaled * 100) / 100;

    return scaledRounded.toString() + suffix;
  }
}
